import { Form, Textarea, TextField } from "@/base";
import { appendBulletPoint } from "@/base/form/bullet-point-textarea/tiptap-bullet-points";
import { useApiServiceContext } from "@/contexts";
import { Button } from "@heroui/react";
import { joiResolver } from "@hookform/resolvers/joi";
import {
    type ApplicantShared,
    ApplicantUpdateDto,
    ApplicantValidationSchemas,
    validationOptions,
} from "@sr-partner-portal/common";
import { IconSend } from "@tabler/icons-react";
import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

export type ApplicantCommentFormParameters = {
    comment: string;
    commenterName: string;
};

interface ApplicantExternalNotesProps {
    applicant: ApplicantShared;
    isDisabled?: boolean;
    shareLinkToken: string;
    isViewOnly?: boolean;
}

const ApplicantExternalComments = ({
    applicant,
    isDisabled,
    shareLinkToken,
    isViewOnly,
}: ApplicantExternalNotesProps) => {
    const apiService = useApiServiceContext();
    const queryClient = useQueryClient();
    const formController = useForm<ApplicantCommentFormParameters>({
        resolver: joiResolver(
            ApplicantValidationSchemas.comment.full.form,
            validationOptions,
        ),
    });

    const [watchedComment, watchedCommenterName] =
        formController.watch([
            "commenterName",
            "comment",
        ]);

    const mutation = useMutation({
        mutationFn: ({
            applicantUpdateDto,
        }: {
            applicantUpdateDto: ApplicantUpdateDto;
        }) => {
            return apiService.applicants.updateByShareLinkTokenAsync(
                shareLinkToken,
                applicantUpdateDto,
            );
        },

        onMutate: async (variables) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: ["sharedApplicant", shareLinkToken],
            });

            // Snapshot the previous value
            const previousApplicants = queryClient.getQueryData([
                "sharedApplicant",
                shareLinkToken,
            ]);

            // Optimistically update to the new value
            queryClient.setQueryData(
                ["sharedApplicant", shareLinkToken],
                (old: ApplicantShared) => ({
                    ...old,
                    customerNotes:
                        variables.applicantUpdateDto.customerNotes,
                }),
            );

            // Return a context object with the snapshotted value
            return { previousApplicants };
        },

        // If the mutation fails,
        // use the context returned from onMutate to roll back
        onError: (_error, _variables, context) => {
            queryClient.setQueryData(
                ["sharedApplicant", shareLinkToken],
                context.previousApplicants,
            );
            toast.error("Speichern des Bewerbers fehlgeschlagen");
        },

        onSuccess: (applicantDto) => {
            toast.success(`Bewerber ${applicantDto.name} gespeichert`, {
                id: "ApplicantNotes_success",
            });

            formController.reset();
        },

        // Always refetch after error or success:
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["sharedApplicant", shareLinkToken],
            });
        }
    });

    const onSubmit = useCallback(
        (data: { comment: string; commenterName: string;}) => {
            if (
                !formController.formState.isDirty ||
                mutation.isPending ||
                !data.comment ||
                !data.commenterName
            ) {
                return;
            }

            const fullComment = `Kommentar von ${data.commenterName.trim()}: ${data.comment.trim()}`;

            mutation.mutate({
                applicantUpdateDto: new ApplicantUpdateDto({
                    customerNotes: appendBulletPoint({
                        bulletPoints: applicant?.customerNotes || "",
                        newBulletPoint: fullComment,
                    }),
                    hasCustomerNoteComments: true,
                }),
            });
        },
        [
            formController.formState.isDirty,
            mutation.mutate,
            mutation.isPending,
            applicant?.customerNotes,
        ],
    );

    return (
        <Form controller={formController} onSubmit={onSubmit}>
            <div className="flex h-full flex-col gap-2">
                <TextField
                    fieldName="commenterName"
                    label="Ihr Name"
                    placeholder="Ihr Name"
                    hideLabel={true}
                    isAutofocusing={true}
                    classNames={{
                        inputWrapper: "h-14 min-h-14 group-data-[focus-visible=true]:!ring-0 group-data-[focus-visible=true]:!ring-offset-0",
                    }}
                />
                <Textarea
                    fieldName="comment"
                    label="Kommentar"
                    placeholder="Kommentar"
                    isDisabled={isDisabled || isViewOnly}
                    hideLabel={true}
                    minRows={1}
                    endContent={
                        <Button
                            type="submit"
                            color="primary"
                            isIconOnly={true}
                            className="self-start"
                            isDisabled={
                                !watchedComment || !watchedCommenterName
                            }
                        >
                            <IconSend />
                        </Button>
                    }
                />
            </div>
        </Form>
    );
};

export { ApplicantExternalComments };
